import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchSharedDesign, API } from "../lib/api";
import FloorPlan2D from "../components/FloorPlan2D";
import { Home, Loader2 } from "lucide-react";
import SEO from "../components/SEO";

/**
 * V1.7.7 — Public read-only share page. No auth required. Shows the floor
 * plan, basic stats, and the AI gallery exterior shot if one exists. Doors
 * the visitor toward signing up to create their own.
 */
export default function SharePage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, design: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchSharedDesign(slug);
        if (!cancelled) setState({ loading: false, design: r.data, error: null });
      } catch (e) {
        if (!cancelled) {
          setState({
            loading: false,
            design: null,
            error: e?.response?.status === 404 ? "not_found" : "fetch_failed",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-zinc-950 grid place-items-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin text-brass-400" />
      </div>
    );
  }
  if (state.error === "not_found") {
    return (
      <div className="min-h-screen bg-zinc-950 grid place-items-center text-center px-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-orange-300 mb-2">
            // 404 · Plan Not Found
          </div>
          <p className="text-zinc-100 text-lg">This shared plan link has expired or was removed.</p>
          <Link
            to="/"
            className="inline-block mt-4 px-4 py-2 bg-brass-600 hover:bg-brass-500 text-white font-mono text-xs uppercase tracking-widest"
          >
            // Build Your Own
          </Link>
        </div>
      </div>
    );
  }

  const d = state.design;
  const hero = (d?.gallery || []).find((s) => /exterior/i.test(s?.label || "") && s.data_url);

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 flex flex-col" data-testid="share-page">
      {/* V2.0.87 — per-share SEO/OG tags. `og:image` points at the
          public /api/share/{slug}/og-image.png endpoint which returns a
          composed before/after PNG (for uploads) or a branded card. */}
      <SEO
        title={d.name || "Shared plan"}
        description={
          d.transformation?.has_upload
            ? `${d.name || "This home"} — designed on Blueprint AI, starting from a hand-drawn sketch. ${d.bedrooms || "?"} BR / ${d.bathrooms || "?"} BA · ${(d.square_footage || 0).toLocaleString()} sqft.`
            : `${d.name || "This home"} — ${d.style || "Modern"} · ${d.bedrooms || "?"} BR / ${d.bathrooms || "?"} BA · ${(d.square_footage || 0).toLocaleString()} sqft. Designed on Blueprint AI.`
        }
        path={`/share/${slug}`}
        image={`${API}/share/${slug}/og-image.png`}
        type="article"
      />
      <header className="bg-zinc-950 text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/logo-mark-80.png"
            alt="Blueprint AI"
            width={36}
            height={36}
            className="w-9 h-9 rounded-full ring-1 ring-brass-500/40"
            data-testid="share-page-logo-mark"
          />
          <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400">
              // Blueprint AI · Shared Plan
            </div>
            <h1 className="font-display text-xl mt-0.5" data-testid="share-project-name">
              {d.name || "Untitled"}
            </h1>
          </div>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brass-600 hover:bg-brass-500 text-white font-mono text-[10px] uppercase tracking-widest"
        >
          <Home className="w-3 h-3" /> // Build Your Own
        </Link>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] flex-1">
        {/* Sidebar: meta + hero image */}
        <aside className="bg-white border-r border-zinc-200 p-5 space-y-4">
          {hero && (
            <img
              src={hero.data_url}
              alt="Exterior hero"
              className="w-full aspect-[4/3] object-cover border border-zinc-300"
              data-testid="share-hero-img"
            />
          )}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border border-zinc-300 py-2">
              <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500">SQFT</div>
              <div className="font-bold text-zinc-900">{(d.square_footage || 0).toLocaleString()}</div>
            </div>
            <div className="border border-zinc-300 py-2">
              <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500">BD</div>
              <div className="font-bold text-zinc-900">{d.bedrooms ?? "—"}</div>
            </div>
            <div className="border border-zinc-300 py-2">
              <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500">BA</div>
              <div className="font-bold text-zinc-900">{d.bathrooms ?? "—"}</div>
            </div>
          </div>
          {d.style && (
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              Style · {d.style}
            </div>
          )}
          {/* V2.0.90 — Opt-in owner credit. Renders when the designer
              has enabled `share_credit_opt_in` on this design. Links to
              the /gallery filter view for that designer. */}
          {d.owner_credit?.display_name && (
            <div
              data-testid="share-owner-credit"
              className="border border-zinc-300 bg-zinc-50 px-3 py-2 text-xs"
            >
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">
                // Designed by
              </div>
              <Link
                to={`/gallery?designer=${encodeURIComponent(d.owner_credit.display_name)}`}
                className="font-bold text-zinc-900 hover:underline"
                data-testid="share-owner-credit-link"
              >
                {d.owner_credit.display_name} →
              </Link>
            </div>
          )}
          {/* V2.0.87 — Show my transformation. If the design owner started
              from an uploaded sketch, visitors see the "before" alongside
              the plan/render — automatic social proof without a manual
              post. */}
          {d.transformation?.has_upload && d.transformation?.before_thumbnail_data_url && (
            <div className="border border-brass-500/50 bg-brass-50 p-3 space-y-2" data-testid="share-transformation">
              <div className="font-mono text-[9px] uppercase tracking-widest text-brass-700">
                // Sketch → Real 3D Home
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <img
                    src={d.transformation.before_thumbnail_data_url}
                    alt="Original sketch"
                    className="w-full aspect-square object-cover border border-brass-300"
                    data-testid="share-transformation-before"
                  />
                  <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500 mt-1 text-center">
                    Sketch
                  </div>
                </div>
                <div>
                  {hero ? (
                    <img
                      src={hero.data_url}
                      alt="Rendered result"
                      className="w-full aspect-square object-cover border border-brass-300"
                      data-testid="share-transformation-after"
                    />
                  ) : d.thumbnail_data_url ? (
                    <img
                      src={d.thumbnail_data_url}
                      alt="Design thumbnail"
                      className="w-full aspect-square object-cover border border-brass-300"
                      data-testid="share-transformation-after"
                    />
                  ) : (
                    <div className="w-full aspect-square border border-brass-300 bg-zinc-100 grid place-items-center font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                      Render
                    </div>
                  )}
                  <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500 mt-1 text-center">
                    Built by AI
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-600 leading-snug">
                This home started as a napkin sketch — Blueprint AI turned it into an editable 3D BIM plan.
              </div>
            </div>
          )}
          <p className="text-zinc-500 text-xs leading-relaxed">
            This is a read-only preview. To copy, edit, or unlock the architectural PDF,
            create your own free account.
          </p>
        </aside>

        {/* Floor plan canvas */}
        <main className="bg-white relative" data-testid="share-floor-plan-host">
          <FloorPlan2D blueprint={d.blueprint_json} />
        </main>
      </div>
    </div>
  );
}
